import React, { useCallback, useMemo, useState } from 'react'
import { To, NavigateFunction, useParams } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'

import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'

import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { CustomFileTypeFilterModal } from 'src/components/Reusable/IndexPage/FilterBar/modals/CustomFileTypeFilterModal'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar'
import { GravatarWithTooltip } from 'src/components/Gravatar/Gravatar'
import { useFileTypeIconForEvidenceGroups } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { FileTypeFilterProvider } from 'src/pages/Evidence/context/FileTypeFilterContext'
import {
  NavigateProvider,
  useNavigateContext,
} from 'src/components/Reusable/Navigation/NavigateContext'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { SearchProvider } from 'src/Utils/search/search.context'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { getEvidenceSourceLabel } from 'src/Utils/Evidence/evidence.helpers'
import {
  useLatestEvidenceGroups,
  useLatestEvidenceGroupsRequestFromParsedQuery,
} from 'src/pages/Evidence/evidence.hooks'
import { EvidenceIndexGridHeader } from 'src/pages/Evidence/grid/EvidenceGridHeader'

import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatISODateTime, formatTimestamp } from 'src/Utils/formatDate'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'

import { ChatContextSelection } from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { MultiSelectModal } from 'src/components/Reusable/MultiSelectModal'
import { ContextSelectorTableText } from '../chatContextPanel.styles'
import { GridRowLinkItem } from '../chatContextPanel.components'
import { EvidenceFilterContainer } from './evidenceModal.styles'

export type IndividualEvidenceSelectorProps = {
  currentSelection: ChatContextSelection
  onCommit: (newSelection: ChatContextSelection) => void
  show: boolean
  hide: () => void
  onBack?: () => void
}

export const IndividualEvidenceSelector = (
  props: IndividualEvidenceSelectorProps,
): JSX.Element => {
  const [parsedQueryParams, setParsedQueryParams] = useState<ParsedQuery>({})
  const navigate: NavigateFunction = useCallback((to: To | number) => {
    if (typeof to === 'number') {
      return
    }
    const search =
      typeof to === 'string'
        ? new URL(to, window.location.origin).search
        : to.search || ''
    const queryParams: ParsedQuery<string> = queryString.parse(search, {
      arrayFormat: 'bracket',
    })
    setParsedQueryParams(queryParams)
  }, [])

  const locationSearchString = useMemo(() => {
    const search = queryString.stringify(parsedQueryParams, {
      arrayFormat: 'bracket',
    })
    return search && `?${search}`
  }, [parsedQueryParams])

  return (
    <NavigateProvider
      navigate={navigate}
      parsedQueryParams={parsedQueryParams}
      locationSearchString={locationSearchString}
    >
      <SearchProvider>
        <IndividualEvidenceSelectorWrapper {...props} />
      </SearchProvider>
    </NavigateProvider>
  )
}

const useSearchableLatestEvidenceGroups = (evidenceGroups: EvidenceGroup[]) =>
  useMemo(
    () =>
      prepareSearchableData(
        evidenceGroups,
        (group) => group.getEvidenceId(),
        (group) => {
          const sourceText = getEvidenceSourceLabel(
            group.getIsAutomated(),
            group.getIsLinkedDocument(),
            group.getServiceModelId(),
            group.getDocImportLink(),
            group.getMime(),
          )

          return [
            group.getCaption(),
            // What the row shows: an automated caption's "--" reads "-".
            formatEvidenceCaption(
              group.getCaption(),
              group.getIsAutomated(),
            ) as string,
            sourceText,
            group.getActorEmail(),
            group.getActorName(),
            group.getCreatedAt() ? formatISODateTime(group.getCreatedAt()) : '',
            group.getRelevantDate()
              ? formatISODateTime(group.getRelevantDate())
              : '',
            group.getDocModifiedAt()
              ? formatISODateTime(group.getDocModifiedAt())
              : '',
            group.getDocScannedAt()
              ? formatISODateTime(group.getDocScannedAt())
              : '',
          ]
        },
        (group) => [group.getContentId() || ''],
      ),
    [evidenceGroups],
  )

const IndividualEvidenceSelectorWrapper = ({
  currentSelection,
  onCommit: onCommitOriginal,
  show,
  hide,
  onBack,
}: IndividualEvidenceSelectorProps): JSX.Element => {
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const params = useParams()
  const req = useLatestEvidenceGroupsRequestFromParsedQuery(
    parsedQueryParams ?? {},
  )

  const fileTypeIcon = useFileTypeIconForEvidenceGroups()
  const { data, isLoading, error } = useLatestEvidenceGroups(req)
  const evidenceItems = useMemo(() => data?.getItemsList() || [], [data])
  const evidenceGroups = useMemo(
    () =>
      evidenceItems
        .map((evidence) => evidence.getEvidenceGroup())
        .filter((group): group is EvidenceGroup => !!group),
    [evidenceItems],
  )
  const searchableEvidence = useSearchableLatestEvidenceGroups(evidenceGroups)
  const { data: matchingEvidenceGroups } = useApplySearch(searchableEvidence)

  const items = useMemo(() => {
    return matchingEvidenceGroups.map((evidenceGroup) => {
      const FileTypeIcon = fileTypeIcon(
        evidenceGroup.getIsAutomated(),
        evidenceGroup.getMime(),
        evidenceGroup.getIsLinkedDocument(),
        evidenceGroup.getServiceModelId(),
      )
      const relevantDate = evidenceGroup.getRelevantDate()
      const createdAt = evidenceGroup.getCreatedAt()

      return {
        id: evidenceGroup.getEvidenceId(),
        renderItems: [
          <FileTypeIcon width="24px" height="24px" key="0-icon" />,
          <ContextSelectorTableText key="1-title">
            <GridRowLinkItem
              link={generatePermalink({
                pageContext: params.pageContext as string,
                modelType: MODEL_TYPE.EVIDENCE,
                modelId: evidenceGroup.getId(),
                isInternalLink: false,
              })}
              text={
                formatEvidenceCaption(
                  evidenceGroup.getCaption(),
                  evidenceGroup.getIsAutomated(),
                ) as string
              }
            />
          </ContextSelectorTableText>,
          <ContextSelectorTableText key="2-controls-count">
            {evidenceGroup.getLinkedControlsCount() || 0}
          </ContextSelectorTableText>,
          <GravatarWithTooltip
            email={evidenceGroup.getActorEmail()}
            name={evidenceGroup.getActorName()}
            key="3-actor"
          />,
          relevantDate ? (
            <ContextSelectorTableText key="4-relevant-date">
              {formatTimestamp(relevantDate, DATE_FORMATS.ISO_WITH_TIME)}
            </ContextSelectorTableText>
          ) : null,
          createdAt ? (
            <ContextSelectorTableText key="5-created-at">
              {formatTimestamp(createdAt, DATE_FORMATS.ISO_WITH_TIME)}
            </ContextSelectorTableText>
          ) : null,
        ],
      }
    })
  }, [fileTypeIcon, matchingEvidenceGroups, params.pageContext])

  const filters = useMemo(
    () => (
      <EvidenceFilterContainer onClick={(e) => void e.preventDefault()}>
        <FileTypeFilterProvider>
          <>
            <FilterBar
              config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.EVIDENCE]}
              searchBarOptions={{ withContentSearch: true }}
              disableSavedFilters
            />
            <CustomFileTypeFilterModal />
          </>
        </FileTypeFilterProvider>
      </EvidenceFilterContainer>
    ),
    [],
  )

  const hasFilteredResults = useMemo(() => {
    return parsedQueryParams && Object.keys(parsedQueryParams).length > 0
  }, [parsedQueryParams])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      if (
        (hasFilteredResults || !currentSelection.includeAll) &&
        newSelection.includeAll
      ) {
        onCommitOriginal({
          includeAll: false,
          included: new Set(items.map((item) => item.id)),
        })
      } else {
        onCommitOriginal(newSelection)
      }
    },
    [onCommitOriginal, hasFilteredResults, items, currentSelection.includeAll],
  )

  const headers = useMemo(
    () => <EvidenceIndexGridHeader isReadonly={true} />,
    [],
  )

  return (
    <MultiSelectModal
      title={'Select Evidence'}
      description={
        'You can also select specific pieces of evidence here. This is additional to selecting by control.'
      }
      itemType="evidence"
      currentSelection={currentSelection}
      onCommit={onCommit}
      show={show}
      hide={hide}
      onBack={onBack}
      isLoading={isLoading}
      items={items}
      numberOfColumns={6}
      error={error}
      filters={filters}
      isFilteredResults={hasFilteredResults}
      modalSize="xl"
      headers={items.length ? headers : null}
    />
  )
}
